import { Controller } from "@hotwired/stimulus"
import { createSelect2Manager } from "../lib/select2_manager"

// Select2-backed picker for list-valued AppSettings (email recipients, org-ID
// and user-ID lists) on Admin → System → App Settings, also used by the VIP
// Customers card's Add VIP & Exempt popup and the Spam Protector agent-number
// popup. The <select> itself is intentionally unnamed so it never posts; on
// every change we write the comma-joined selection into the hidden field (the
// hiddenValue target — `app_setting[value]` on App Settings), so the server
// receives a single string value.
//
// Values:
//   multiple    — multi-select (default true)
//   tags        — allow free-typed entries (default true: email lists accept
//                 non-admin mailboxes; org lists pass false so only real
//                 organizations are selectable)
//   placeholder — dropdown placeholder text
//   ajaxUrl     — optional. When set, the picker fetches type-ahead suggestions
//                 from this endpoint (admin users search) instead of relying on
//                 static <option>s. Results are keyed by USER ID (the value
//                 these settings store), labeled "Name (email)", and non-admin
//                 users only. Already-stored ids are still rendered
//                 server-side as selected <option>s. Pass tags:false — a
//                 free-typed entry can't be a real account id.
//
// Usage (see app/views/admin/app_settings/_form.html.haml):
//   div[data-controller="app-setting-list-select"]
//     input[type=hidden name="app_setting[value]"
//           data-app-setting-list-select-target="hiddenValue"]
//     select[data-app-setting-list-select-target="select"] > option[selected]…
export default class extends Controller {
  static targets = ["select", "hiddenValue"]
  static values = {
    multiple: { type: Boolean, default: true },
    tags: { type: Boolean, default: true },
    placeholder: { type: String, default: "Select…" },
    ajaxUrl: { type: String, default: "" }
  }

  connect () {
    this.select2Manager = createSelect2Manager()
    this.initSelect2()
  }

  disconnect () {
    this.select2Manager.destroyAll()
  }

  initSelect2 () {
    if (!this.hasSelectTarget) return

    const controller = this
    // Anchor the dropdown inside the modal so it isn't clipped by the modal's
    // overflow and stays above the backdrop (Bootstrap modal z-index).
    const modal = this.element.closest(".modal")

    this.select2Manager.init(
      this.selectTarget,
      () => this.select2Options(modal),
      {
        onInitialized (_element, $element) {
          controller.$select = $element
          controller.sync()
          $element.on("change", () => controller.sync())
          return () => $element.off("change")
        }
      }
    )
  }

  // Base Select2 config, plus AJAX type-ahead when an ajaxUrl is configured.
  select2Options (modal) {
    const options = {
      multiple: this.multipleValue,
      tags: this.tagsValue,
      tokenSeparators: [","],
      width: "100%",
      placeholder: this.placeholderValue,
      allowClear: !this.multipleValue,
      dropdownParent: modal ? window.jQuery(modal) : undefined
    }

    if (this.ajaxUrlValue) {
      options.minimumInputLength = 2
      options.ajax = {
        url: this.ajaxUrlValue,
        dataType: "json",
        delay: 250,
        // Only ever suggest non-admin users for these account-list settings.
        data: (params) => ({ q: params.term, exclude_admins: "true" }),
        // Key each option by the user id the endpoint returns — the value
        // these settings store — labeled "Name (email)" (the endpoint's text).
        processResults: (data) => ({
          results: data.map(user => ({ id: user.id, text: user.text }))
        }),
        cache: true
      }
    }

    return options
  }

  // Mirror the current selection into the hidden value field as a comma list.
  sync () {
    if (!this.hasHiddenValueTarget || !this.$select) return

    const selected = this.$select.val()
    const values = Array.isArray(selected) ? selected : [selected].filter(Boolean)
    const next = values.map(v => String(v).trim()).filter(Boolean).join(", ")
    if (next === this.hiddenValueTarget.value) return

    this.hiddenValueTarget.value = next
    // Select2 fires a jQuery-only change, so announce the new value natively:
    // the app-setting modal's Save is dirty-gated on input/change (BUTTON-9).
    this.hiddenValueTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
